import type { ReactNode } from "react";
import { Plus } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import type { RecordSummary } from "@shared/recordSummary";
import { RailPeople, type RailPerson } from "./RailPeople";
import { RailFtcContact } from "./RailFtcContact";
import { RailDetails } from "./RailDetails";
import { RailAddresses } from "./RailAddresses";
import { RailNotes } from "./RailNotes";
import { RailDocuments } from "./RailDocuments";
import { SuppressionBadge } from "./SuppressionBadge";
import { EnrichPersonButton } from "./EnrichPersonButton";
import { primaryContact } from "./recordFormat";

/** The rail every company page shares: People, FTC contact, Details, Addresses, Notes, Documents, then the page's extras. */
export function CompanyRail({ company, summary, details, detailsExtra, notesTitle, onSaveNotes, onAddPerson, children }: {
  company: { id: string; notes: string | null; contacts?: RailPerson[] | null };
  summary: { data?: RecordSummary; isLoading: boolean; isError: boolean };
  details: [string, ReactNode][];
  detailsExtra?: ReactNode;
  notesTitle?: string;
  onSaveNotes: (v: string) => Promise<unknown>;
  onAddPerson: () => void;
  children?: ReactNode;
}) {
  const qc = useQueryClient();
  return (
    <>
      <RailPeople
        title="People"
        people={company.contacts ?? []}
        emptyText="No one added yet."
        action={
          <button type="button" onClick={onAddPerson} className="inline-flex items-center gap-1 text-xs font-semibold text-primary-text hover:underline">
            <Plus className="h-3 w-3" />Add
          </button>
        }
        rowExtras={(p) => (
          <>
            <SuppressionBadge email={p.email ?? null} />
            <EnrichPersonButton person={p} onDone={() => qc.invalidateQueries({ queryKey: ["company", company.id] })} />
          </>
        )}
      />
      <RailFtcContact rows={summary.data?.ftcContact} loading={summary.isLoading} error={summary.isError} />
      <RailDetails rows={details}>{detailsExtra}</RailDetails>
      <RailAddresses companyId={company.id} defaultAddressee={primaryContact(company.contacts ?? [])?.name ?? null} />
      <RailNotes title={notesTitle} value={company.notes} onSave={onSaveNotes} />
      <RailDocuments companyId={company.id} />
      {children}
    </>
  );
}
